import type { EventInput } from '@fullcalendar/react';
import type { AuthenticatedUser } from '@src/features/users';
import {
  addDays,
  formatIsoDate,
  isSameDate,
  parseIsoDate,
} from '@src/shared/lib';
import { useCallback, useMemo } from 'react';
import {
  type AbsenceBlock,
  absenceSelectionToSlot,
  buildHolidayLookup,
  getActiveAbsenceIds,
  getHolidayExclusiveEndDate,
  groupAbsencesByUserDate,
  hasMixedHourlyTypes,
  holidayAppliesToBranch,
  usersToCalendarResources,
} from '../lib';
import type {
  Absence,
  AbsenceWarnings,
  HolidayDate,
  ResourceUser,
  SelectedAbsence,
  TimelineSlot,
  VacationBalances,
  VisibleRange,
} from '../types';
import { absencesToEvents, getTransientEventsForSlot } from '.';

type UseTimelineCalendarDataParams = {
  absences: Absence[];
  blocks: AbsenceBlock[];
  balances: VacationBalances;
  currentUser: AuthenticatedUser | null;
  /** The row drawn as selected: the searched user, else the selected one. */
  highlightedUserId: number | null;
  holidays: HolidayDate[];
  isDayView: boolean;
  isMonthView: boolean;
  selectedAbsence: SelectedAbsence | null;
  selectedSlot: TimelineSlot | null;
  users: ResourceUser[];
  visibleRange: VisibleRange;
  warnings: AbsenceWarnings;
};

export default function useTimelineCalendarData({
  absences,
  blocks,
  balances,
  currentUser,
  highlightedUserId,
  holidays,
  isDayView,
  isMonthView,
  selectedAbsence,
  selectedSlot,
  users,
  visibleRange,
  warnings,
}: UseTimelineCalendarDataParams) {
  const activeSelection = useMemo(
    () =>
      selectedSlot ??
      (selectedAbsence ? absenceSelectionToSlot(selectedAbsence) : null),
    [selectedAbsence, selectedSlot],
  );

  const allAbsencesByUserDate = useMemo(
    () => groupAbsencesByUserDate(absences),
    [absences],
  );

  const collapseMonthViewAbsences = useCallback(
    (items: Absence[]) => {
      if (isDayView) {
        return items;
      }

      const itemGroups = groupAbsencesByUserDate(items);

      return Array.from(itemGroups.values(), (group) => {
        const [firstAbsence] = group;
        const key = `${firstAbsence.userId}:${firstAbsence.dateStr}`;
        const fullGroup = allAbsencesByUserDate.get(key) ?? group;

        if (!hasMixedHourlyTypes(fullGroup)) {
          return firstAbsence;
        }

        return {
          ...firstAbsence,
          useNeutralColour: true,
        };
      });
    },
    [allAbsencesByUserDate, isDayView],
  );

  // Month view only: the weekend is already a grey column, so let the resources
  // call it business time and leave the non-business fill to the days that are
  // specific to one employee's contract.
  const timelineResources = useMemo(
    () =>
      usersToCalendarResources(
        users,
        currentUser,
        balances,
        isMonthView,
        warnings,
      ),
    [balances, currentUser, isMonthView, users, warnings],
  );

  const holidayLookup = useMemo(() => buildHolidayLookup(holidays), [holidays]);

  /**
   * Whether a day inside an absence is drawn in the lighter shade.
   *
   * The same two kinds of day `.abs-weekend` and `.abs-holiday` cover on a
   * single-day bar -- a weekend, or a holiday in this employee's branch. An
   * aggregated bar is one element, so `useMarkedDayBand` paints these as bands
   * instead of as a whole-element background, and the two paths have to agree
   * on which days they are or a run would shade differently from the single
   * days either side of it.
   */
  const isLightDay = useCallback(
    (date: string, userId: number | null) => {
      const day = parseIsoDate(date);
      if (!day) {
        return false;
      }
      const weekday = day.getDay();
      if (weekday === 0 || weekday === 6) {
        return true;
      }
      const branchId =
        userId == null
          ? null
          : (users.find((user) => user.id === userId)?.branch?.id ?? null);
      return holidayLookup.isHolidayDate(day, branchId);
    },
    [holidayLookup, users],
  );

  /**
   * Only the holidays that do *not* apply everywhere.
   *
   * Weekend, today and global-holiday shading moved to `slotLaneClass`, which
   * paints one full-height column and so covers the group rows and the strip
   * above the first lane. A branch holiday cannot go there: it is true of some
   * employees and not others, so it stays one background event per affected
   * row.
   */
  const holidayBackgroundEvents = useMemo(() => {
    if (!isMonthView || holidays.length === 0) {
      return [];
    }

    return holidays.flatMap((holiday) => {
      if (holiday.branchId === null) {
        return [];
      }
      const startDateIso = holiday.date;
      const endDateIso = getHolidayExclusiveEndDate(holiday);

      return users.flatMap((user) => {
        if (!holidayAppliesToBranch(holiday, user.branch?.id)) {
          return [];
        }

        return [
          {
            id: `holiday-bg-user-${user.id}-${startDateIso}`,
            resourceId: `user-${user.id}`,
            start: startDateIso,
            end: endDateIso,
            allDay: true,
            display: 'background' as const,
            className: 'absence-holiday-background',
          },
        ];
      });
    });
  }, [holidays, isMonthView, users]);

  /**
   * The selected row's fill, on its free days only.
   *
   * Weekend, global-holiday and today shading is a column *behind* the lane, so
   * a lane background either hides it or tints it. One background event per
   * free day leaves those cells as they are. Branch holidays and the
   * employee's own days off are skipped as well: both are already drawn over
   * the lane. Day view has no day shading and keeps the plain lane background.
   */
  const selectedRowBackgroundEvents = useMemo(() => {
    if (!isMonthView || highlightedUserId == null) {
      return [];
    }
    const resourceId = `user-${highlightedUserId}`;
    const resource = timelineResources.find((item) => item.id === resourceId);
    if (!resource) {
      return [];
    }
    const branchId = users.find((user) => user.id === highlightedUserId)?.branch
      ?.id;
    // Month-view resources count the weekend as business time, so the weekend
    // is excluded explicitly below. An employee without a schedule has no
    // business hours at all; their weekdays still count as free, or most
    // selected rows would get no fill.
    const workingDays = new Set(
      resource.businessHours.flatMap((hours) => hours.daysOfWeek),
    );
    const today = new Date();
    const isFreeDay = (date: Date) => {
      const day = date.getDay();
      return (
        day !== 0 &&
        day !== 6 &&
        (workingDays.size === 0 || workingDays.has(day)) &&
        !holidayLookup.isHolidayDate(date, branchId) &&
        !isSameDate(date, today)
      );
    };

    // One event per day rather than per run: each carries the day's grid line
    // (see `.absence-selected-row-background`), which the opaque fill would
    // otherwise hide.
    const events: EventInput[] = [];
    const end = addDays(visibleRange.end, 0);
    for (
      let date = addDays(visibleRange.start, 0);
      date < end;
      date = addDays(date, 1)
    ) {
      if (!isFreeDay(date)) {
        continue;
      }
      const dateIso = formatIsoDate(date);
      events.push({
        id: `selected-row-bg-${highlightedUserId}-${dateIso}`,
        resourceId,
        start: dateIso,
        end: formatIsoDate(addDays(date, 1)),
        allDay: true,
        display: 'background',
        className: 'absence-selected-row-background',
      });
    }
    return events;
  }, [
    highlightedUserId,
    holidayLookup,
    isMonthView,
    timelineResources,
    users,
    visibleRange,
  ]);

  const selectedAbsenceEvents = useMemo(() => {
    if (!isDayView || selectedAbsence?.isMultiEvent) {
      return [];
    }

    const activeIds = getActiveAbsenceIds(activeSelection);

    if (!activeIds.length) {
      return [];
    }

    const selectedAbsences = absences.filter((absence) =>
      activeIds.includes(absence.id),
    );

    if (!selectedAbsences.length) {
      return [];
    }

    return absencesToEvents(
      activeSelection,
      collapseMonthViewAbsences(selectedAbsences),
      isDayView,
      blocks,
    );
  }, [
    absences,
    activeSelection,
    blocks,
    collapseMonthViewAbsences,
    isDayView,
    selectedAbsence?.isMultiEvent,
  ]);

  const timelineEvents = useMemo(() => {
    let transientEvents: EventInput[] = [];
    if (selectedSlot) {
      transientEvents = getTransientEventsForSlot(
        selectedSlot,
        absences,
        users,
        isDayView,
      );
    }

    const activeIds =
      !isDayView || selectedAbsence?.isMultiEvent
        ? []
        : getActiveAbsenceIds(activeSelection);
    const selectedIds = new Set(activeIds);
    const selectedDateKeys = new Set(
      absences
        .filter((absence) => selectedIds.has(absence.id))
        .map((absence) => `${absence.userId}:${absence.dateStr}`),
    );
    const visibleAbsences = collapseMonthViewAbsences(
      absences.filter((absence) => {
        if (isDayView) {
          return !selectedIds.has(absence.id);
        }
        return !selectedDateKeys.has(`${absence.userId}:${absence.dateStr}`);
      }),
    );
    const visibleAbsenceEvents = absencesToEvents(
      null,
      visibleAbsences,
      isDayView,
      blocks,
    );
    const backgroundEvents = isMonthView
      ? [...holidayBackgroundEvents, ...selectedRowBackgroundEvents]
      : [];

    return [
      ...backgroundEvents,
      ...visibleAbsenceEvents,
      ...selectedAbsenceEvents,
      ...(transientEvents.length > 0 ? transientEvents : []),
    ];
  }, [
    absences,
    activeSelection,
    collapseMonthViewAbsences,
    blocks,
    holidayBackgroundEvents,
    isDayView,
    isMonthView,
    selectedAbsence?.isMultiEvent,
    selectedAbsenceEvents,
    selectedRowBackgroundEvents,
    selectedSlot,
    users,
  ]);

  return {
    activeSelection,
    holidayLookup,
    isLightDay,
    timelineEvents,
    timelineResources,
  };
}
